import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import { Button } from '../components/Button';
import { Checkbox } from '../components/Checkbox';
import { cn } from '../utils/cn';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { ProtectedLayout } from '../components/ProtectedLayout';

type IntentionState = {
  accepted: boolean;
  heard: boolean;
  valued: boolean;
  inspired: boolean;
  safe: boolean;
  motivated: boolean;
  supported: boolean;
  appreciated: boolean;
  matter: boolean;
  energized: boolean;
  clarity: boolean;
  worthwhile: boolean;
};

export function ImpactIntentions() {
  const navigate = useNavigate();
  const [intentions, setIntentions] = useState<IntentionState>({
    accepted: false,
    heard: false,
    valued: false,
    inspired: false,
    safe: false,
    motivated: false,
    supported: false,
    appreciated: false,
    matter: false,
    energized: false,
    clarity: false,
    worthwhile: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (key: keyof IntentionState) => {
    setIntentions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setError('');
  };

  const selectedCount = Object.values(intentions).filter(Boolean).length;

  const handleNext = async () => {
    if (isSubmitting) return;

    const selectedIntentions = Object.entries(intentions)
      .filter(([_, value]) => value)
      .map(([key]) => key);

    if (selectedIntentions.length === 0) {
      setError('Please select at least one intention');
      return;
    }

    if (selectedIntentions.length > 5) {
      setError('Please select no more than 5 intentions');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError || !user) {
        setError('No authenticated user found. Please sign in again.');
        return;
      }

      const { error: insertError } = await supabase
        .from('impact_intentions')
        .insert(
          selectedIntentions.map(intention => ({
            user_id: user.id,
            intention_type: intention
          }))
        );

      if (insertError) throw insertError;

      navigate('/core-word');
    } catch (error: any) {
      console.error('Error saving impact intentions:', error);
      setError(error.message || 'Failed to save your intentions. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ProtectedLayout>
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg p-8">
          <p className="text-gray-500 mb-6">
            To help you get ahead, we need to know your core values. This takes three questions and only has to be done once but can be updated later.
          </p>

          <div className="flex items-center gap-3 mb-6">
            <Heart className="w-8 h-8 text-[#236caf]" />
            <h1 className="text-3xl font-bold text-[#004060]">Impact Intention</h1>
          </div>

          <p className="text-xl text-gray-600 mb-8">
            When you interact with others, how do you want them to feel? Select 1-5 intentions:
            {selectedCount > 0 && (
              <span className="text-[#236caf] font-medium ml-2">
                ({selectedCount}/5 selected)
              </span>
            )}
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {Object.entries(intentions).map(([key, value]) => (
              <Checkbox
                key={key}
                label={key.charAt(0).toUpperCase() + key.slice(1)}
                checked={value}
                onChange={() => handleChange(key as keyof IntentionState)}
              />
            ))}
          </div>

          <Button
            onClick={handleNext}
            disabled={isSubmitting || selectedCount === 0 || selectedCount > 5}
            className={cn(
              'w-full',
              (isSubmitting || selectedCount === 0 || selectedCount > 5) && 'opacity-50 cursor-not-allowed'
            )}
          >
            {isSubmitting ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </div>
    </ProtectedLayout>
  );
}
